/**
 * Modal action button — circle trigger that expands left into a pill (same chrome as modal title).
 * Use in modal header top-right; hover/focus expands the pill to the left so the label stays on-screen.
 * Replaces tooltip-above for actions that would otherwise tooltip off-screen.
 */
import type { ReactNode } from "react"
import { useState, useRef, useEffect } from "react"

export interface ModalActionButtonProps {
  /** Icon shown in the expanded pill (e.g. Sparkles). */
  icon: React.ComponentType<{ className?: string }>
  /** Short label in the pill (e.g. "Glitch"). */
  label: string
  /** Optional second line (e.g. "Turn off glitch mode"). */
  explainer?: string
  /** Button content — usually the circle content (character, icon). */
  children: ReactNode
  /** Click handler. */
  onClick?: () => void
  /** aria-label for the button. */
  "aria-label": string
  /** Optional callback when pill opens (e.g. trigger burst). */
  onExpand?: () => void
}

export function ModalActionButton({
  icon: Icon,
  label,
  explainer,
  children,
  onClick,
  "aria-label": ariaLabel,
  onExpand,
}: ModalActionButtonProps) {
  const [expanded, setExpanded] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const open = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = undefined
    setExpanded(true)
    onExpand?.()
  }

  const close = () => {
    timeoutRef.current = setTimeout(() => setExpanded(false), 100)
  }

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current) }, [])

  return (
    <div
      className="flex items-center justify-end gap-3"
      onMouseEnter={open}
      onMouseLeave={close}
    >
      <div
        className="overflow-hidden transition-[max-width] duration-200 ease-out flex items-center shrink-0"
        style={{ maxWidth: expanded ? 220 : 0 }}
      >
        <div className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md whitespace-nowrap">
          <span className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
            <Icon className="w-3 h-3 text-white/60" />
          </span>
          <div className="flex flex-col min-w-0">
            <span className="text-luster-200 text-[10px] font-bold tracking-[0.2em] uppercase leading-tight">
              {label}
            </span>
            {explainer && (
              <span className="text-[9px] text-white/50 tracking-wide leading-tight mt-0.5">
                {explainer}
              </span>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onClick}
        onFocus={open}
        onBlur={close}
        className="w-8 h-8 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center transition-colors cursor-pointer text-white/70 hover:text-white/90 focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-0 focus:ring-offset-transparent shrink-0"
        aria-label={ariaLabel}
      >
        {children}
      </button>
    </div>
  )
}
